import React, { useCallback, useState, useEffect, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useHistory } from 'react-router';
import classnames from 'classnames';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';
import { useApp } from 'Shared/utils/hooks/use-app';

// Utils
import PageUrlsUtils from 'Shared/utils/PageUrls.utils';
import * as WorkspaceMeetingModeUtil from 'Shared/utils/WorkspaceMeetingMode.utils';
import { getCurrentMoment, convertMomentToUnix } from 'Shared/utils/DateTime.utils';

// Actions
import * as WorkspaceMeetingModeActions from 'Shared/actions/WorkspaceMeetingMode.actions';
import * as UsersActions from 'Shared/actions/Users.actions';

// Selectors
import * as WorkspaceMeetingModeSelectors from 'Shared/selectors/WorkspaceMeetingMode.selectors';
import * as UsersSelectors from 'Shared/selectors/Users.selectors';
import { getUserFlag } from 'Shared/selectors/user';

// Helpers
import WorkspaceMeetingModeHelper from 'Shared/model_helpers/WorkspaceMeetingMode.helper';

// Components
// import { Popover, PopoverBody } from 'reactstrap';
import { WpButtonGroup, WpButton } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { usePopover, WpPopoverOverlay } from 'Wpds/components/popover';

// Contexts
import MobileContext from 'Shared/contexts/MobileContext';

// Styles
import './StartMeetingModeButton.scss';

const StartMeetingModeButton = () => {
  const { workspaceId } = useParams();

  const history = useHistory();
  const dispatch = useDispatch();
  const isMobile = useContext(MobileContext);

  const [isShowingTooltip, toggleIsShowingTooltip] = useState(false);

  const { isOneOnOne, users } = useWorkspace();

  const btnVariant = isOneOnOne && users.length === 1 ? 'secondary' : 'primary';
  const hasSeenFlag = Boolean(useSelector(state => getUserFlag(state, { flag: 'seen_meeting_mode_v2_popover' })));

  const { isZoom } = useApp();

  const activeMeetingMode = useSelector(state =>
    WorkspaceMeetingModeSelectors.getWorkspaceMeetingModeItemByWorkspaceSlug(state, { workspaceSlug: workspaceId })
  );
  const dismissedPopoverList = useSelector(state =>
    WorkspaceMeetingModeSelectors.getMeetingModeDismissedPopoverList(state)
  );
  const participantIds = WorkspaceMeetingModeHelper.getParticipantIds(activeMeetingMode);
  const meetingModeId = WorkspaceMeetingModeHelper.getId(activeMeetingMode);

  const isPopoverDismissed = WorkspaceMeetingModeUtil.isParticipantPopoverDismissed(
    dismissedPopoverList,
    activeMeetingMode
  );

  const { id: currentUserId } = useCurrentUser();
  const otherUserId = participantIds && participantIds.filter(id => id !== currentUserId).first();
  const otherUser =
    useSelector(state => UsersSelectors.getUserByUserId(state, { userId: otherUserId }))?.toJS() || null;

  const popover = usePopover({
    open: isShowingTooltip && !isPopoverDismissed,
    onOpenChange: toggleIsShowingTooltip,
    placement: 'top',
  });

  useEffect(() => {
    dispatch(WorkspaceMeetingModeActions.fetchMeetingModeStatus(workspaceId));
  }, [workspaceId]);

  useEffect(() => {
    const hasAdditionalParticipants =
      participantIds && participantIds.size > 0 && participantIds.filter(id => id !== currentUserId).size > 0;
    const isAlreadyInMeeting = participantIds && participantIds.size > 0 && participantIds.includes(currentUserId);
    if (hasAdditionalParticipants && !isAlreadyInMeeting) {
      toggleIsShowingTooltip(true);
    } else {
      toggleIsShowingTooltip(false);
    }
  }, [activeMeetingMode, toggleIsShowingTooltip, participantIds, currentUserId]);

  const trackSeenNewPopover = () => {
    const timestamp = convertMomentToUnix(getCurrentMoment());
    dispatch(UsersActions.updateUserFlag('seen_meeting_mode_v2_popover', timestamp));
  };

  const handleStartMeetingClick = useCallback(() => {
    if (!hasSeenFlag) {
      trackSeenNewPopover();
    }

    toggleIsShowingTooltip(false);
    history.push({ pathname: PageUrlsUtils.makeWorkspaceMeetingModeUrl(workspaceId) });
  }, [history, workspaceId, toggleIsShowingTooltip, hasSeenFlag]);

  const handleDismissClick = () => {
    toggleIsShowingTooltip(false);
    dispatch(WorkspaceMeetingModeActions.dismissParticipantPopover(meetingModeId));
  };

  const baseClassName = 'StartMeetingModeButton_V2';

  const getPopoverMessage = () => {
    if (!otherUserId) return null;

    if (participantIds.size === 1)
      return <>👋 &nbsp;{otherUser.displayName} is already in the room. Enter to join them!</>;

    return <>👋 &nbsp;{participantIds.size} people are already in the room. Enter to join them!</>;
  };

  const classes = classnames(baseClassName, isZoom && isMobile ? `${baseClassName}--in-zoom` : false);

  const isBlock = isZoom && isMobile;
  const size = isZoom && isMobile ? 'lg' : 'md';

  return (
    <div className={classes}>
      <WpButton
        size={size}
        onClick={handleStartMeetingClick}
        block={isBlock}
        ref={popover.targetRef}
        variant={btnVariant}
        qaTest="enter-discussion-room-button"
      >
        <WpIcon name="presentation" />
        Start Meeting
      </WpButton>
      <WpPopoverOverlay context={popover}>
        {getPopoverMessage()}
        <div className={`${baseClassName}__tooltip__buttons`}>
          <WpButtonGroup>
            <WpButton variant="secondary" size="sm" onClick={handleStartMeetingClick}>
              Join
            </WpButton>
            <WpButton variant="tertiary" size="sm" onClick={handleDismissClick}>
              Dismiss
            </WpButton>
          </WpButtonGroup>
        </div>
      </WpPopoverOverlay>
    </div>
  );
};

export default StartMeetingModeButton;
